import { toMiraMode } from "@/data/models/MiraMode";
import { ClientContractData } from "@/data/models/ClientAdministrationRow";
import {
  ClientSetupDeviceUpdates,
  ClientSetupFunctions,
  ClientSetupLicenceKey,
  ClientSetupMaster,
  ClientSetupOptions,
  IotUpdateCondition,
} from "@/data/models/ClientSetup";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { CONTRACT_TYPE_ORDER } from "@/utils/ClientContracts";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { chatModeFromFlags } from "@/utils/ChatMode";
import { PRODUCT_SWITCHES } from "@/utils/ClientProducts";
import {
  ClientSetupDraft,
  PROJECT_ORGANISATION_QUESTIONS,
  followsMaster,
  licenceCount,
  neededLicences,
  possibleProjectTypeIds,
  setupExternalSystemEmail,
} from "@/utils/ClientSetupDraft";
import { partnerSystemLabelKey } from "@/utils/ClientPartners";
import { masterDataValue } from "@/utils/ClientMasterData";
import { projectOrganisationText } from "@/utils/ClientProjectOrganisation";

/**
 * The Zusammenfassung step of the guided setup (#2610082): everything ANLEGEN will write, one section
 * per step, with the labels the rest of IRES Office uses for the same values. Pure.
 */

type Translate = (key: string, values?: unknown[]) => string;

/** One line: what, and its value. */
export interface SummaryLine {
  label: string;
  value: string;
}

/** One step's lines. */
export interface SummarySection {
  step: string;
  title: string;
  lines: SummaryLine[];
}

const DETAIL = "admin.clients.detail";
const SETUP = "admin.clients.setup";

/** The Funktionen switches in the order the step shows them, each with the label Systemkonfiguration uses. */
export const FUNCTION_SWITCH_LABEL_KEYS: Array<{ field: keyof ClientSetupFunctions; labelKey: string }> = [
  { field: "enableTimeTracking", labelKey: "settings.timeTracking.title" },
  { field: "enableGeoEncoding", labelKey: "settings.system.geoEncoding" },
  { field: "enableTracking", labelKey: "settings.system.tracking" },
  { field: "enableAllDevicesManagement", labelKey: "settings.allDevicesManagement.title" },
  { field: "enableDeviceManagementTile", labelKey: "settings.showDeviceManagementInCockpit.title" },
  { field: "enableVehicleManagement", labelKey: "settings.vehicleManagement.title" },
  { field: "enableScheduling", labelKey: "settings.system.operationalPlaning" },
];

// The answer texts live in the leaf the Projektorganisation card shares (2026-10-29).
export { projectOrganisationText, projectSettingsOptionText } from "@/utils/ClientProjectOrganisation";

/** The ASSIST choice as Systemkonfiguration shows it: off, or on with its activation scope. */
export function assistModeKey(functions: Partial<ClientSetupFunctions>): string {
  return functions.enableAiPredictions === true ? (functions.renoActivationScope ?? "ACTIVE_AND_NEW_ONLY") : "OFF";
}

/** The functions switched on, by name; "–" for none. */
export function enabledFunctionsText(functions: Partial<ClientSetupFunctions>, t: Translate): string {
  const on = FUNCTION_SWITCH_LABEL_KEYS.filter((entry) => functions[entry.field] === true).map((entry) => t(entry.labelKey));
  return on.length > 0 ? on.join(", ") : EMPTY_VALUE;
}

/** Each licence's label, as IRES Office's licence screens name them ("Lizenzen Basis"). */
export const LICENCE_LABEL_KEYS: Record<ClientSetupLicenceKey, string> = {
  basic: "assignLicences.basicLicences",
  damageAssessmentAndLeakDetection: "assignLicences.damageAssessmentAndLeakDetectionLicences",
  dryingAndRepair: "assignLicences.dryingAndRepairLicences",
};

/** The two IoT update conditions with IRES Admin's labels, in the order it shows them. */
export const DEVICE_UPDATE_LABEL_KEYS: Array<{ field: keyof ClientSetupDeviceUpdates; labelKey: string }> = [
  { field: "updateFirmwareViaIotCondition", labelKey: `${SETUP}.contracts.updateFirmwareViaIot` },
  { field: "updateConfigViaIotCondition", labelKey: `${SETUP}.contracts.updateConfigViaIot` },
];

/** A condition by IRES Admin's name ("Panel Idle / Hochgefahren"); "–" where none is stored. */
export function iotUpdateConditionText(condition: IotUpdateCondition | null | undefined, t: Translate): string {
  return condition ? t(`${SETUP}.contracts.conditions.${condition}`) : EMPTY_VALUE;
}

function contractText(ticked: boolean, sinceMillis: number | null, t: Translate): string {
  if (!ticked) {
    return t("common.no");
  }
  const since = sinceMillis !== null ? formatDateToDayMonthYear(new Date(sinceMillis)) : null;
  return since ? t(`${SETUP}.contracts.yesSince`, [since]) : t("common.yes");
}

/** The Verträge as the Verträge card names them: the types, then each contract with its start date. */
export function contractLines(contracts: ClientContractData, t: Translate): SummaryLine[] {
  const types = CONTRACT_TYPE_ORDER.filter((type) => contracts.contractTypes.includes(type)).map((type) => t(`${DETAIL}.contractTypes.${type}`));
  return [
    { label: t(`${DETAIL}.fields.contracts`), value: types.length > 0 ? types.join(", ") : EMPTY_VALUE },
    {
      label: t(`${DETAIL}.fields.serviceContract`),
      value: contractText(contracts.hasServiceContractForFirmwaresAndConfigs, contracts.hasServiceContractForFirmwaresAndConfigsSince, t),
    },
    {
      label: t(`${DETAIL}.fields.warrantyExtensionForPanels`),
      value: contractText(contracts.hasWarrantyExtensionForPanels, contracts.hasWarrantyExtensionForPanelsSince, t),
    },
  ];
}

/** The two IoT update conditions, by IRES Admin's labels. */
export function deviceUpdateLines(updates: Partial<Record<keyof ClientSetupDeviceUpdates, IotUpdateCondition | null>>, t: Translate): SummaryLine[] {
  return DEVICE_UPDATE_LABEL_KEYS.map((entry) => ({ label: t(entry.labelKey), value: iotUpdateConditionText(updates[entry.field], t) }));
}

function typesText(ids: number[], t: Translate): string {
  return ids.length > 0 ? ids.map((id) => t(`projectDetails.projectTypeCodes.${id}`)).join(", ") : EMPTY_VALUE;
}

/** The Externe Systeme step (#2610084, merge 4): the one new system, or a member's links to its master's. */
function externalSystemsSection(draft: ClientSetupDraft, master: ClientSetupMaster | null, t: Translate): SummarySection {
  const title = t(`${SETUP}.steps.externalSystems`);
  if (followsMaster(draft)) {
    const names = (master?.externalSystems ?? [])
      .filter((system) => draft.linkedExternalSystemIds.includes(system.id))
      .map((system) => system.name);
    return { step: "externalSystems", title, lines: [{ label: t(`${SETUP}.externalSystems.linked`), value: names.length > 0 ? names.join(", ") : EMPTY_VALUE }] };
  }
  if (!draft.withExternalSystem) {
    return { step: "externalSystems", title, lines: [{ label: t(`${SETUP}.externalSystems.system`), value: t(`${SETUP}.externalSystems.none`) }] };
  }
  const system = draft.externalSystem;
  return {
    step: "externalSystems",
    title,
    lines: [
      { label: t(`${SETUP}.externalSystems.system`), value: `${system.name.trim()} (${t(partnerSystemLabelKey(system.system === "NONE" ? null : system.system))})` },
      { label: t("admin.clients.partners.access.email"), value: setupExternalSystemEmail(draft).trim().toLowerCase() || EMPTY_VALUE },
    ],
  };
}

/**
 * The summary of a setup.
 *
 * @param draft - the setup.
 * @param options - what IRES Cloud offered (the possible types).
 * @param master - a member's master with its settings, else null.
 * @param t - the translator.
 * @param formatCount - formats a count for the current locale (the screens pass `formatNumber`).
 * @returns one section per step.
 */
export function setupSummary(
  draft: ClientSetupDraft,
  options: ClientSetupOptions | null,
  master: ClientSetupMaster | null,
  t: Translate,
  formatCount: (value: number) => string = String
): SummarySection[] {
  const text = (field: keyof ClientSetupDraft["masterData"]) => masterDataValue(field, draft.masterData[field]) ?? EMPTY_VALUE;
  const member = followsMaster(draft);
  const address = [
    [masterDataValue("street", draft.masterData.street), masterDataValue("houseNumber", draft.masterData.houseNumber)].filter(Boolean).join(" "),
    [masterDataValue("zip", draft.masterData.zip), masterDataValue("city", draft.masterData.city)].filter(Boolean).join(" "),
  ].filter((part) => part !== "").join(", ");
  const groupValue = draft.groupKind === "MEMBER"
    ? t(`${SETUP}.group.memberOf`, [master?.name ?? EMPTY_VALUE])
    : t(`${SETUP}.group.kinds.${draft.groupKind}`);
  const operation = draft.notLiveReason === null
    ? t("admin.clients.liveOperation.live")
    : t("admin.clients.liveOperation.notLiveWithReason", [t(`admin.clients.liveOperation.reasons.${draft.notLiveReason}`)]);

  const products = PRODUCT_SWITCHES.filter((product) => draft.products[product]).map((product) => t(`${DETAIL}.fields.${product}`));
  const followsLine = (label: string): SummaryLine => ({ label, value: t(`${SETUP}.followsMaster`) });

  const functions = member ? master?.functions ?? {} : draft.functions;
  const organisation = member ? master?.projectOrganisation ?? {} : draft.projectOrganisation;
  const hasProjects = member || possibleProjectTypeIds(options, draft.products).length > 0;
  const licences = neededLicences(draft.products);

  return [
    {
      step: "masterData",
      title: t(`${SETUP}.steps.masterData`),
      lines: [
        { label: t("admin.clients.masterData.clientName"), value: text("clientName") },
        { label: t(`${DETAIL}.fields.clientNumber`), value: text("clientNumber") },
        { label: t(`${DETAIL}.fields.clientShorthand`), value: text("iresProjectNumberPrefix") },
        { label: t(`${SETUP}.address`), value: address || EMPTY_VALUE },
        { label: t(`${DETAIL}.fields.clientType`), value: t(`${DETAIL}.clientTypes.${draft.masterData.clientType}`) },
        { label: t(`${SETUP}.signInEmail`), value: draft.email.trim().toLowerCase() || EMPTY_VALUE },
        { label: t(`${DETAIL}.fields.liveOperation`), value: operation },
        { label: t(`${DETAIL}.sections.group`), value: groupValue },
      ],
    },
    {
      step: "products",
      title: t(`${SETUP}.steps.products`),
      lines: [{ label: t(`${DETAIL}.sections.products`), value: products.length > 0 ? products.join(", ") : t(`${SETUP}.products.officeOnly`) }],
    },
    {
      step: "projectTypes",
      title: t(`${SETUP}.steps.projectTypes`),
      lines: [
        member
          ? { label: t(`${DETAIL}.sections.projectTypes`), value: `${typesText(master?.projectTypeIds ?? [], t)} (${t(`${SETUP}.followsMaster`)})` }
          : { label: t(`${DETAIL}.sections.projectTypes`), value: hasProjects ? typesText(draft.projectTypeIds, t) : t(`${SETUP}.noProjects`) },
      ],
    },
    {
      step: "projectOrganisation",
      title: t(`${SETUP}.steps.projectOrganisation`),
      lines: !hasProjects
        ? [{ label: t(`${SETUP}.steps.projectOrganisation`), value: t(`${SETUP}.noProjects`) }]
        : member && master?.projectOrganisation == null
          ? [followsLine(t(`${SETUP}.steps.projectOrganisation`))]
          : PROJECT_ORGANISATION_QUESTIONS.map((question) => ({
            label: t(`settings.projectManagement.projectSettings.${question}`),
            value: projectOrganisationText(organisation[question], t),
          })),
    },
    {
      step: "functions",
      title: t(`${SETUP}.steps.functions`),
      lines: member && master?.functions == null
        ? [followsLine(t(`${SETUP}.steps.functions`))]
        : [
          { label: t(`${SETUP}.functions.enabled`), value: enabledFunctionsText(functions, t) },
          {
            label: t("settings.system.chat.title"),
            value: t(`settings.system.chat.mode.${chatModeFromFlags(functions.enablePersonalChat === true, functions.enableProjectChat === true)}`),
          },
          { label: t("settings.aiPredictions.title"), value: t(`settings.aiPredictions.mode.${assistModeKey(functions)}`) },
          { label: t("settings.system.mira.title"), value: t(`settings.system.mira.mode.${toMiraMode(functions.miraMode)}`) },
        ],
    },
    {
      step: "licences",
      title: t(`${SETUP}.steps.licences`),
      lines: licences.length === 0
        ? [{ label: t(`${SETUP}.steps.licences`), value: t(`${SETUP}.licences.noneNeeded`) }]
        : licences.map((key) => {
          const count = licenceCount(draft.licences[key]);
          return { label: t(LICENCE_LABEL_KEYS[key]), value: count === null ? EMPTY_VALUE : formatCount(count) };
        }),
    },
    {
      step: "contracts",
      title: t(`${SETUP}.steps.contracts`),
      lines: member
        ? [
          ...(master?.contracts ? contractLines(master.contracts, t) : [followsLine(t(`${DETAIL}.sections.contracts`))]),
          ...(master?.deviceUpdates ? deviceUpdateLines(master.deviceUpdates, t) : []),
          followsLine(t(`${DETAIL}.sections.targetVersions`)),
        ]
        : [
          ...contractLines(draft.contracts, t),
          ...deviceUpdateLines(draft.deviceUpdates, t),
          { label: t(`${DETAIL}.sections.targetVersions`), value: t(`${SETUP}.contracts.followsFleet`) },
        ],
    },
    {
      step: "users",
      title: t(`${SETUP}.steps.users`),
      lines: [
        { label: t(`${SETUP}.users.iresAdmin`), value: t(`${SETUP}.users.iresAdminAlways`) },
        {
          label: t(`${SETUP}.users.firstUser`),
          value: draft.withFirstUser
            ? `${draft.firstUser.firstName.trim()} ${draft.firstUser.lastName.trim()} (${draft.firstUser.nickname.trim()})`
            : t(`${SETUP}.users.noFirstUser`),
        },
      ],
    },
    ...(draft.products.interface ? [externalSystemsSection(draft, master, t)] : []),
  ];
}

